import React, { useEffect, useMemo, useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

import type { BLPRenewalStatusDto } from "../../api/getBLPDashboard";

const COLORS = {
  "Not Started": "#1E6FBA",
  "In Progress": "#38BDF8",
  Submitted: "#DB2777",
  Completed: "#1E3A6E",
};

const STATUS_ORDER = [
  "Not Started",
  "In Progress",
  "Submitted",
  "Completed",
] as const;

type RenewalStatusName = (typeof STATUS_ORDER)[number];

type RenewalStatusItem = {
  name: RenewalStatusName;
  value: number;
  percent: number;
  color: string;
};

interface RenewalStatusChartProps {
  data?: BLPRenewalStatusDto;
  isLoading?: boolean;
  isError?: boolean;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  fontSize?: number;
}

interface ResponsiveChartSettings {
  chartHeight: number;
  innerRadius: number;
  outerRadius: number;
  labelFontSize: number;
  labelOffset: number;
  labelLineOffset: number;
  pieHorizontalMargin: number;
  pieVerticalMargin: number;
  tooltipFontSize: number;
}

const getResponsiveChartSettings = (): ResponsiveChartSettings => {
  if (globalThis.window === undefined) {
    return {
      chartHeight: 230,
      innerRadius: 55,
      outerRadius: 90,
      labelFontSize: 10,
      labelOffset: 15,
      labelLineOffset: 14,
      pieHorizontalMargin: 72,
      pieVerticalMargin: 2,
      tooltipFontSize: 12,
    };
  }

  const viewportWidth = globalThis.window.innerWidth;

  if (viewportWidth >= 1920) {
    return {
      chartHeight: 320,
      innerRadius: 80,
      outerRadius: 130,
      labelFontSize: 13,
      labelOffset: 22,
      labelLineOffset: 20,
      pieHorizontalMargin: 110,
      pieVerticalMargin: 4,
      tooltipFontSize: 14,
    };
  }

  if (viewportWidth >= 1536) {
    return {
      chartHeight: 280,
      innerRadius: 68,
      outerRadius: 112,
      labelFontSize: 12,
      labelOffset: 19,
      labelLineOffset: 18,
      pieHorizontalMargin: 92,
      pieVerticalMargin: 3,
      tooltipFontSize: 13,
    };
  }

  return {
    chartHeight: 230,
    innerRadius: 55,
    outerRadius: 90,
    labelFontSize: 10,
    labelOffset: 15,
    labelLineOffset: 14,
    pieHorizontalMargin: 72,
    pieVerticalMargin: 2,
    tooltipFontSize: 12,
  };
};

const useResponsiveChartSettings = (): ResponsiveChartSettings => {
  const [settings, setSettings] = useState<ResponsiveChartSettings>(
    getResponsiveChartSettings,
  );

  useEffect(() => {
    if (globalThis.window === undefined) {
      return;
    }

    const handleResize = () => {
      setSettings(getResponsiveChartSettings());
    };

    globalThis.window.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      globalThis.window.removeEventListener("resize", handleResize);
    };
  }, []);

  return settings;
};

const normalizeStatus = (raw: unknown): RenewalStatusName | null => {
  if (typeof raw !== "string" || raw.trim() === "") {
    return "Not Started";
  }

  const key = raw
    .trim()
    .toLowerCase()
    .replaceAll(/[_-]+/g, " ")
    .replaceAll(/\s+/g, " ");

  const statusMap: Record<string, RenewalStatusName> = {
    "not started": "Not Started",
    notstarted: "Not Started",
    "in progress": "In Progress",
    inprogress: "In Progress",
    submitted: "Submitted",
    completed: "Completed",
    complete: "Completed",
  };

  return statusMap[key] ?? null;
};

const CustomTooltip = ({
  active,
  payload,
  fontSize = 12,
}: CustomTooltipProps): React.ReactElement | null => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const item = payload[0].payload as RenewalStatusItem;

  return (
    <div
      className="pointer-events-none rounded-md border border-blue-100 bg-white px-3 py-2 text-center shadow-md"
      style={{
        fontSize,
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}
    >
      <p className="font-bold text-black">
        {item.value.toLocaleString()} ({item.percent.toFixed(1)}%)
      </p>

      <p
        className="font-bold"
        style={{
          color: item.color,
        }}
      >
        {item.name}
      </p>
    </div>
  );
};

const RenewalStatusChart: React.FC<RenewalStatusChartProps> = ({
  data,
  isLoading = false,
  isError = false,
}) => {
  const {
    chartHeight,
    innerRadius,
    outerRadius,
    labelFontSize,
    labelOffset,
    labelLineOffset,
    pieHorizontalMargin,
    pieVerticalMargin,
    tooltipFontSize,
  } = useResponsiveChartSettings();

  const { chartData, pieData, total } = useMemo(() => {
    const counts: Record<RenewalStatusName, number> = {
      "Not Started": 0,
      "In Progress": 0,
      Submitted: 0,
      Completed: 0,
    };

    (data?.statuses ?? []).forEach((item) => {
      const normalizedStatus = normalizeStatus(item.renewalStatus);

      if (!normalizedStatus) {
        return;
      }

      const statusCount = Number(item.statusCount ?? 0);

      if (!Number.isNaN(statusCount)) {
        counts[normalizedStatus] += statusCount;
      }
    });

    const calculatedTotal = STATUS_ORDER.reduce(
      (sum, status) => sum + counts[status],
      0,
    );

    const apiTotal = Number(data?.total ?? 0);

    const finalTotal = apiTotal > 0 ? apiTotal : calculatedTotal;

    const normalizedChartData: RenewalStatusItem[] = STATUS_ORDER.map(
      (status) => {
        const value = counts[status];

        return {
          name: status,
          value,
          percent: finalTotal > 0 ? (value / finalTotal) * 100 : 0,
          color: COLORS[status],
        };
      },
    );

    return {
      total: finalTotal,
      chartData: normalizedChartData,
      pieData: normalizedChartData.filter((item) => item.value > 0),
    };
  }, [data]);

  const renderPieLabel = (props: any): React.ReactElement => {
    const {
      cx = 0,
      cy = 0,
      midAngle = 0,
      outerRadius: renderedOuterRadius = 0,
      index = 0,
    } = props || {};

    const item = pieData[index];

    if (!item || item.value <= 0) {
      return <g />;
    }

    const radian = Math.PI / 180;

    const parsedOuterRadius =
      typeof renderedOuterRadius === "number"
        ? renderedOuterRadius
        : Number.parseFloat(String(renderedOuterRadius)) || 0;

    const labelRadius = parsedOuterRadius + labelOffset;

    const x = cx + labelRadius * Math.cos(-midAngle * radian);

    const y = cy + labelRadius * Math.sin(-midAngle * radian);

    return (
      <text
        x={x}
        y={y}
        dy={4}
        textAnchor={x > cx ? "start" : "end"}
        fontWeight={800}
        fontSize={labelFontSize}
        fontFamily="'Segoe UI', system-ui, sans-serif"
        fill="#111827"
      >
        {item.value.toLocaleString()} ({item.percent.toFixed(1)}%)
      </text>
    );
  };

  const renderLabelLine = (props: any): React.ReactElement => {
    const {
      cx = 0,
      cy = 0,
      midAngle = 0,
      outerRadius: renderedOuterRadius = 0,
      index = 0,
    } = props || {};

    const item = pieData[index];

    if (!item || item.value <= 0) {
      return <g />;
    }

    const radian = Math.PI / 180;

    const parsedOuterRadius =
      typeof renderedOuterRadius === "number"
        ? renderedOuterRadius
        : Number.parseFloat(String(renderedOuterRadius)) || 0;

    const startX = cx + parsedOuterRadius * Math.cos(-midAngle * radian);

    const startY = cy + parsedOuterRadius * Math.sin(-midAngle * radian);

    const middleX =
      cx +
      (parsedOuterRadius + labelLineOffset * 0.4) *
        Math.cos(-midAngle * radian);

    const middleY =
      cy +
      (parsedOuterRadius + labelLineOffset * 0.4) *
        Math.sin(-midAngle * radian);

    const endX =
      cx + (parsedOuterRadius + labelLineOffset) * Math.cos(-midAngle * radian);

    const endY =
      cy + (parsedOuterRadius + labelLineOffset) * Math.sin(-midAngle * radian);

    return (
      <polyline
        points={`${startX},${startY} ${middleX},${middleY} ${endX},${endY}`}
        stroke={item.color}
        strokeWidth={labelFontSize >= 14 ? 1.5 : 1}
        fill="none"
      />
    );
  };

  let chartContent: React.ReactNode;

  if (isLoading) {
    chartContent = (
      <div
        className="flex w-full items-center justify-center font-sans text-xs text-gray-500 2xl:text-sm"
        style={{
          height: chartHeight,
        }}
      >
        Loading...
      </div>
    );
  } else if (isError) {
    chartContent = (
      <div
        className="flex w-full items-center justify-center font-sans text-xs text-red-600 2xl:text-sm"
        style={{
          height: chartHeight,
        }}
      >
        Unable to load renewal status data.
      </div>
    );
  } else if (total === 0) {
    chartContent = (
      <div
        className="flex w-full items-center justify-center font-sans text-xs text-gray-400 2xl:text-sm"
        style={{
          height: chartHeight,
        }}
      >
        No renewal status data
      </div>
    );
  } else {
    chartContent = (
      <div
        className="w-full"
        style={{
          height: chartHeight,
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart
            margin={{
              top: pieVerticalMargin,
              right: pieHorizontalMargin,
              bottom: pieVerticalMargin,
              left: pieHorizontalMargin,
            }}
          >
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              minAngle={4}
              paddingAngle={2}
              cornerRadius={4}
              stroke="#ffffff"
              strokeWidth={1}
              labelLine={renderLabelLine}
              label={renderPieLabel}
            >
              {pieData.map((item) => (
                <Cell key={item.name} fill={item.color} />
              ))}
            </Pie>

            <Tooltip
              content={<CustomTooltip fontSize={tooltipFontSize} />}
              cursor={false}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="shrink-0 rounded-t-xl border-b border-blue-100 bg-blue-50 px-4 py-2">
        <div className="flex items-center justify-between gap-3">
          <h2 className="m-0 text-left font-sans text-sm font-bold text-primary 2xl:text-base">
            Renewal Status
          </h2>

          <p className="m-0 whitespace-nowrap font-sans text-sm italic text-primary">
            Total of{" "}
            <span className="font-bold text-primary">
              {total.toLocaleString()}
            </span>
          </p>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 items-center justify-center px-4">
          {chartContent}
        </div>

        {!isLoading && !isError && total > 0 && (
          <div className="shrink-0 border-t border-gray-100 px-4 py-2">
            <div className="grid grid-cols-[repeat(4,max-content)] items-center justify-center gap-x-3 whitespace-nowrap 2xl:gap-x-5">
              {chartData.map((item) => (
                <div
                  key={item.name}
                  className="flex items-center gap-2 whitespace-nowrap font-sans text-xs tracking-normal text-gray-700 2xl:text-sm"
                >
                  <span
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-full 2xl:h-3 2xl:w-3"
                    style={{
                      backgroundColor: item.color,
                    }}
                  />

                  <span>
                    {item.name} ({item.value.toLocaleString()})
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RenewalStatusChart;
